import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Modal from './Modal';
import Swal from 'sweetalert2';
import authStorage from '../../../utils/authStorage';
import { Helmet } from 'react-helmet-async';

// ─── Shared UI Pieces ───────────────────────────────────────────
const BackgroundBlobs = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-[-1]">
        <div className="absolute top-[-5%] right-[-10%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-orange-400 opacity-20 blur-3xl" />
        <div className="absolute top-[50%] left-[-15%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-red-400 opacity-10 blur-3xl transform -translate-y-1/2" />
    </div>
);

const AuthNav = ({ rightText, rightLinkText, rightLinkTo }) => (
    <nav className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center relative z-10">
        <div>
            <div className="flex items-center select-none" dir="ltr">
                <span className="font-logo font-light text-2xl sm:text-3xl text-slate-800 tracking-wide">
                    BrokersConnect
                </span>
            </div>
        </div>
        <div className="flex items-center">
            {rightText && <span className="text-slate-500 ml-4 hidden md:inline text-sm">{rightText}</span>}
            <Link to={rightLinkTo} className="px-5 py-2 text-sm sm:text-base rounded-full border-2 border-slate-200 text-slate-700 font-bold hover:border-primary hover:text-primary transition-all bg-white/50 backdrop-blur-sm">
                {rightLinkText}
            </Link>
        </div>
    </nav>
);

// Eye‑open SVG
const EyeOpen = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

// Eye‑closed SVG
const EyeClosed = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
);

// ─── Main Component ─────────────────────────────────────────────
const LoginPage = ({ handleLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Forgot‑password flow states
    const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-success'
    const [resetEmail, setResetEmail] = useState('');

    // Modal (licence activation)
    const [showModal, setShowModal] = useState(false);
    const [errorType, setErrorType] = useState('');
    const [isMobile] = useState(false);
    const [isSubscriberScreen, setSubscriberScreen] = useState(false);

    const seoData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "BrokersConnect | מערכת לניתוח אחזקות גופים מוסדיים - התחברות",
      "description": "התחברות למערכת BrokersConnect - פלטפורמת המודיעין הפיננסי לניתוח אחזקות גופים מוסדיים בשוק ההון.",
      "url": "https://brokersconnect.co.il/login",
      "potentialAction": {
        "@type": "Action",
        "target": "https://brokersconnect.co.il/login"
      }
    };

    // Override body overflow:hidden for auth pages
    useEffect(() => {
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';
        return () => { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; };
    }, []);

    const handleModalSubmit = async (data, setData, onClose) => {
        try {
            const response = await axios.post('/accounts/licence-activation/', data);
            if (response.status === 200) {
                Swal.fire({
                    title: "הטופס נשלח בהצלחה",
                    text: "אנו ניצור איתך קשר באמצעות דואר אלקטרוני או טלפון בהקדם,",
                    icon: 'info',
                    confirmButtonText: 'Close',
                    willClose: () => {
                        setData({ fullName: "", email: "", phoneNumber: "", licenseCount: "", freeText: "" });
                        onClose();
                    }
                });
            } else {
                Swal.fire({
                    title: "הטופס נשלח בהצלחה",
                    text: "אנו ניצור איתך קשר באמצעות דואר אלקטרוני או טלפון בהקדם,",
                    icon: 'info',
                    confirmButtonText: 'Close',
                    willClose: () => { onClose(); }
                });
            }
        } catch (err) {
            console.error("There was an error submitting the form data:", err);
        }
    };

    // ─── Login handler ────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!email || !password) { setError('חובה למלא את שני השדות.'); setLoading(false); return; }

        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
        if (!emailRegex.test(email)) { setError('כתובת המייל אינה תקינה.'); setLoading(false); return; }

        try {
            const response = await axios.post('/accounts/login/', { email, password });

            if (response.status === 200) {
                const data = response.data;
                authStorage.setItem('email', email, rememberMe);
                authStorage.setItem('refreshToken', data.refresh, rememberMe);
                authStorage.setItem('accessToken', data.access, rememberMe);
                if (data.session_token) authStorage.setItem('sessionToken', data.session_token, rememberMe);
                navigate('/');
                handleLogin();
                setError(''); setErrorType('');
            } else {
                setError('שם משתמש או סיסמה אינם נכונים');
                setErrorType('');
            }
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setError(err.response.data.detail); setErrorType('');
            } else if (err.response && err.response.status === 403) {
                const detail = err?.response?.data?.detail || '';
                if (detail.includes('המערכת בשימוש במחשב אחר')) {
                    setError(detail); setErrorType('');
                } else {
                    setSubscriberScreen(true); setShowModal(true);
                    setErrorType(err?.response?.data);
                }
            } else {
                setError('חלה תקלה בנסיון ההתחברות, אנא נסה שוב מאוחר יותר'); setErrorType('');
            }
        }
        setLoading(false);
    };

    // ─── Forgot‑password handler ──
    const handleSendResetEmail = async (e) => {
        e.preventDefault();
        setLoading(true);

        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
        if (!emailRegex.test(resetEmail)) { setError('כתובת המייל אינה תקינה.'); setLoading(false); return; }

        try {
            await axios.post('/accounts/forgot_password/', { email: resetEmail });
            setView('forgot-success');
            setError('');
        } catch (err) {
            Swal.fire({ title: "איפוס סיסמה", text: "אירעה שגיאה", icon: 'error', confirmButtonText: 'סגור' });
        }
        setLoading(false);
    };

    // ─── RENDER ─────────────────────────────────────────────────
    return (
        <div dir="rtl" className="bg-background text-slate-800 font-sans overflow-x-hidden max-w-[100vw] selection:bg-primary selection:text-white relative min-h-screen flex flex-col">
            <Helmet>
                <title>BrokersConnect | מערכת לניתוח אחזקות גופים מוסדיים - התחברות</title>
                <meta name="description" content="התחברות למערכת BrokersConnect - פלטפורמת המודיעין הפיננסי לניתוח אחזקות גופים מוסדיים, קופות גמל, קרנות פנסיה וביטוח בשוק ההון." />
                <link rel="canonical" href="https://brokersconnect.co.il/login" />
                <script type="application/ld+json">
                    {JSON.stringify(seoData)}
                </script>
            </Helmet>
            <BackgroundBlobs />

            {/* ─── Login View ─── */}
            {view === 'login' && (
                <>
                    <AuthNav rightText="עדיין אין לך חשבון?" rightLinkText="הרשם עכשיו" rightLinkTo="/signup" />

                    <div className="flex-1 flex flex-col justify-center items-center px-4 w-full relative z-10 pb-12 sm:pb-20">
                        <div className="w-full max-w-md relative">
                            <div className="absolute inset-0 bg-primary opacity-5 transform translate-y-3 translate-x-3 rounded-3xl" />

                            <form onSubmit={handleSubmit} className="relative bg-surface p-8 sm:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100">
                                <div className="text-center mb-6">
                                    <h1 className="text-2xl font-bold text-slate-800 tracking-tight">BrokersConnect</h1>
                                    <p className="text-xs sm:text-sm text-slate-500 mt-1">מערכת לניתוח אחזקות גופים מוסדיים בשוק ההון</p>
                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        <h2 className="text-base font-bold text-slate-700">הזן פרטי גישה</h2>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {/* Email */}
                                    <div>
                                        <label className="block text-slate-600 text-xs sm:text-sm font-bold mb-1.5">מייל</label>
                                        <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} required placeholder="name@company.com"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 px-4 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" dir="ltr" />
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <label className="block text-slate-600 text-xs sm:text-sm font-bold mb-1.5">סיסמה</label>
                                        <div className="relative">
                                            <input id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} required placeholder="••••••••"
                                                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 pl-4 pr-11 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" dir="ltr" />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-primary transition-colors focus:outline-none" title="הצג/הסתר סיסמה">
                                                {showPassword ? <EyeClosed /> : <EyeOpen />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Remember + Forgot */}
                                <div className="flex items-center justify-between mt-5">
                                    <label className="flex items-center cursor-pointer group">
                                        <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer" />
                                        <span className="ml-2 text-sm text-slate-600 mr-2 group-hover:text-slate-800 transition-colors">זכור אותי</span>
                                    </label>
                                    <button type="button" onClick={() => { setView('forgot'); setError(''); }}
                                        className="text-sm font-semibold text-primary hover:text-primaryDark transition-colors">
                                        שכחת סיסמה?
                                    </button>
                                </div>

                                {/* Error */}
                                {error && (
                                    <div className="mt-4 bg-red-50 border border-red-200 text-red-600 text-sm font-bold rounded-lg p-3 text-center">
                                        {error}
                                    </div>
                                )}

                                {/* Submit */}
                                <button type="submit" disabled={loading}
                                    className="mt-8 w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60">
                                    {loading ? 'מתחבר...' : 'התחבר'}
                                </button>
                            </form>
                        </div>
                    </div>
                </>
            )}

            {/* ─── Forgot Password View ─── */}
            {view === 'forgot' && (
                <>
                    <AuthNav rightText="נזכרת בסיסמה?" rightLinkText="חזור להתחברות" rightLinkTo="#"
                        // We override navigation to stay in SPA
                    />
                    {/* Override the Link click for SPA behavior */}
                    <div className="flex-1 flex flex-col justify-center items-center px-4 w-full relative z-10 pb-12 sm:pb-20">
                        <div className="w-full max-w-md relative">
                            <div className="absolute inset-0 bg-primary opacity-5 transform translate-y-3 translate-x-3 rounded-3xl" />

                            <form onSubmit={handleSendResetEmail} className="relative bg-surface p-8 sm:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100">
                                <div className="text-center mb-8">
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-50 mb-5">
                                        <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">שכחת את הסיסמה?</h2>
                                    <p className="text-sm sm:text-base text-slate-500 mt-3 leading-relaxed px-2">
                                        הזן את כתובת המייל באמצעותה נרשמת לאתר ואנו נשלח לך מייל מאובטח לשינוי הסיסמה
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-slate-600 text-xs sm:text-sm font-bold mb-1.5">מייל</label>
                                        <input type="email" value={resetEmail} onChange={(e) => { setResetEmail(e.target.value); setError(''); }} required placeholder="name@company.com"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 px-4 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" dir="ltr" />
                                    </div>
                                </div>

                                {error && (
                                    <div className="mt-4 bg-red-50 border border-red-200 text-red-600 text-sm font-bold rounded-lg p-3 text-center">{error}</div>
                                )}

                                <button type="submit" disabled={loading}
                                    className="mt-8 w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60">
                                    {loading ? 'שולח...' : 'שלח'}
                                </button>

                                <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col items-center gap-3">
                                    <p className="text-sm text-slate-600">
                                        אין לך עדיין חשבון?{' '}
                                        <Link to="/signup" className="font-bold text-primary hover:text-primaryDark transition-colors">הרשם</Link>
                                    </p>
                                    <button type="button" onClick={() => { setView('login'); setError(''); }}
                                        className="text-sm font-semibold text-slate-500 hover:text-primary transition-colors">
                                        חזרה להתחברות
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </>
            )}

            {/* ─── Forgot‑Password Success View ─── */}
            {view === 'forgot-success' && (
                <>
                    <AuthNav rightLinkText="" rightLinkTo="/login" />
                    <div className="flex-1 flex flex-col justify-center items-center px-4 w-full relative z-10 pb-12 sm:pb-20">
                        <div className="w-full max-w-md relative">
                            <div className="absolute inset-0 bg-primary opacity-5 transform translate-y-3 translate-x-3 rounded-3xl" />
                            <div className="relative bg-surface p-8 sm:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-50 ring-4 ring-green-50/50 mb-6">
                                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">המייל נשלח בהצלחה!</h2>
                                <p className="text-base text-slate-500 leading-relaxed mb-8">
                                    שלחנו לך קישור לאיפוס הסיסמה לכתובת המייל שהזנת.<br />
                                    <span className="text-sm opacity-80 mt-2 block">
                                        * אל תשכח לבדוק גם את תיקיית הספאם (דואר זבל) ליתר ביטחון.
                                    </span>
                                </p>
                                <button type="button" onClick={() => { setView('login'); setError(''); setResetEmail(''); }}
                                    className="inline-block w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                                    חזור למסך ההתחברות
                                </button>
                                <div className="mt-6 pt-5 border-t border-slate-100">
                                    <p className="text-sm text-slate-500">
                                        לא קיבלת את המייל?{' '}
                                        <button type="button" onClick={() => setView('forgot')} className="font-bold text-primary hover:text-primaryDark transition-colors">נסה שוב</button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Licence Modal (preserved) */}
            <Modal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleModalSubmit}
                errorType={errorType} isMobile={isMobile} isSubscriberScreen={isSubscriberScreen} />
        </div>
    );
};

export default LoginPage;
